import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    rulesEngineSelectors,
    CharacterUtils,
    Constants,
    FormatUtils,
    RuleData,
    serviceDataActions,
    Vehicle,
    VehicleComponent,
    VehicleComponentUtils,
    VehicleUtils,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { VehicleBlockComponent, VehicleBlockShell } from '../../../components/VehicleBlock';
import VehicleHealthAdjuster from '../../../components/VehicleHealthAdjuster';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { ComponentUtils, PaneComponentEnum, PaneIdentifierUtils } from '../../../utils';
import { PaneIdentifiersVehicleComponent } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    vehicles: Array<Vehicle>;
    identifiers: PaneIdentifiersVehicleComponent | null;
    isReadonly: boolean;
    ruleData: RuleData;
    theme: CharacterTheme;
}
interface State {
    vehicle: Vehicle | null;
    component: VehicleComponent | null;
}
class VehicleComponentPane extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { vehicles, identifiers } = this.props;

        if (vehicles !== prevProps.vehicles || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { vehicles, identifiers } = props;

        let foundVehicle: Vehicle | null | undefined = null;
        let foundComponent: VehicleComponent | null | undefined = null;
        if (identifiers !== null) {
            foundVehicle = vehicles.find((vehicle) => identifiers.vehicleId === VehicleUtils.getMappingId(vehicle));
            if (foundVehicle) {
                foundComponent = VehicleUtils.getComponents(foundVehicle).find(
                    (component) => identifiers.id === VehicleComponentUtils.getMappingId(component),
                );
            }
        }

        return {
            vehicle: foundVehicle ? foundVehicle : null,
            component: foundComponent ? foundComponent : null,
        };
    };

    handleParentClick = (): void => {
        const { dispatch } = this.props;
        const { vehicle } = this.state;

        if (vehicle) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.VEHICLE,
                    PaneIdentifierUtils.generateVehicle(VehicleUtils.getMappingId(vehicle)),
                ),
            );
        }
    };

    handleHealthAdjusterSave = (hitPointDiff: number): void => {
        const { component } = this.state;
        const { dispatch } = this.props;

        if (component === null) {
            return;
        }

        let id = VehicleComponentUtils.getMappingId(component);
        let hitPointInfo = VehicleComponentUtils.getHitPointInfo(component);

        if (hitPointInfo === null) {
            return;
        }

        let newHitPoints = CharacterUtils.calculateHitPoints(hitPointInfo, hitPointDiff);

        dispatch(serviceDataActions.vehicleComponentMappingHitPointsSet(id, newHitPoints.newRemovedHp));
    };

    renderBlock = (): React.ReactNode => {
        const { vehicle, component } = this.state;
        const { ruleData, theme } = this.props;

        if (vehicle === null || component === null) {
            return;
        }

        return (
            <div className="ct-vehicle-component-pane__block">
                <VehicleBlockShell
                    displayType={VehicleUtils.getConfigurationValue(
                        Constants.VehicleConfigurationKeyEnum.DISPLAY_TYPE,
                        vehicle,
                    )}
                >
                    <VehicleBlockComponent
                        {...ComponentUtils.generateVehicleBlockComponentProps(component, vehicle, ruleData)}
                        theme={theme}
                        shouldCoalesce={false}
                    />
                </VehicleBlockShell>
            </div>
        );
    };

    renderName = (): React.ReactNode => {
        const { component } = this.state;

        if (component === null) {
            return null;
        }

        let name = VehicleComponentUtils.getName(component);

        if (name === null) {
            return null;
        }

        let nameText: Array<string> = [];
        const typeNames = VehicleComponentUtils.getTypeNames(component);
        if (typeNames.length > 0 && !typeNames.includes(name)) {
            nameText.push(FormatUtils.renderNonOxfordCommaList(typeNames));
        }

        nameText.push(name);

        return nameText.join(': ');
    };

    render() {
        const { vehicle, component } = this.state;
        const { isReadonly } = this.props;

        if (vehicle === null || component === null) {
            return <PaneInitFailureContent />;
        }

        const hitPointInfo = VehicleComponentUtils.getHitPointInfo(component);

        return (
            <div className="ct-vehicle-component-pane" key={VehicleComponentUtils.getUniqueKey(component)}>
                <SidebarHeader parent={VehicleUtils.getName(vehicle)} onParentClick={this.handleParentClick}>
                    {this.renderName()}
                </SidebarHeader>
                {hitPointInfo !== null && (
                    <VehicleHealthAdjuster
                        hitPointInfo={hitPointInfo}
                        isInteractive={!isReadonly}
                        initiallyCollapsed={false}
                        onSave={this.handleHealthAdjusterSave}
                    />
                )}
                {this.renderBlock()}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        isReadonly: appEnvSelectors.getIsReadonly(state),
        vehicles: rulesEngineSelectors.getVehicles(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
    };
}

export default connect(mapStateToProps)(VehicleComponentPane);
